import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from './useAuth';

export const useSheets = () => {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);

  // Fetch sheets
  useEffect(() => {
    if (!user) {
      setSheets([]);
      setLoading(false);
      return;
    }

    // Simple query without orderBy (sorting done client-side to avoid index requirement)
    const q = query(
      collection(db, 'sheets'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const sheetsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by createdAt descending
        sheetsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return dateB - dateA;
        });
        console.log('Sheets loaded:', sheetsData.length);
        setSheets(sheetsData);
        setLoading(false);
      },
      (err) => {
        console.error('Sheets error:', err.message);
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user]);

  // Fetch folders
  useEffect(() => {
    if (!user) {
      setFolders([]);
      return;
    }

    // Simple query without orderBy (sorting done client-side to avoid index requirement)
    const q = query(
      collection(db, 'folders'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        let foldersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by name ascending
        foldersData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        console.log('Folders loaded:', foldersData.length);
        setFolders(foldersData);
      },
      (err) => {
        console.error('Folders error:', err.message);
      }
    );

    return unsubscribe;
  }, [user]);

  // Create a new folder
  const createFolder = async (name, parentId = null) => {
    if (!user) throw new Error('Must be logged in');
    
    try {
      const folderDoc = await addDoc(collection(db, 'folders'), {
        userId: user.uid,
        name,
        parentId,
        createdAt: new Date().toISOString(),
        color: getRandomFolderColor(),
      });
      return folderDoc.id;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update folder
  const updateFolder = async (folderId, updates) => {
    try {
      await updateDoc(doc(db, 'folders', folderId), {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Delete folder
  const deleteFolder = async (folderId) => {
    try {
      // Move sheets in this folder to root (no folder)
      const sheetsInFolder = sheets.filter(s => s.folderId === folderId);
      for (const sheet of sheetsInFolder) {
        await updateSheet(sheet.id, { folderId: null });
      }
      
      // Delete the folder
      await deleteDoc(doc(db, 'folders', folderId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const uploadSheet = async (file, metadata) => {
    if (!user) throw new Error('Must be logged in to upload');
    
    try {
      // Upload file to Storage
      const fileRef = ref(storage, `${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const fileUrl = await getDownloadURL(fileRef);
      
      // Create Firestore document
      const sheetDoc = await addDoc(collection(db, 'sheets'), {
        userId: user.uid,
        title: metadata.title || file.name.replace(/\.[^/.]+$/, ''),
        composer: metadata.composer || '',
        instrument: metadata.instrument || '',
        genre: metadata.genre || '',
        tags: metadata.tags || [],
        folderId: metadata.folderId || null,
        fileUrl,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        pageCount: metadata.pageCount || 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isFavorite: false,
        isPublic: false,
        shareId: null,
        pageTurnSettings: {
          mode: 'timer',
          bpm: 120,
          measuresPerPage: 8,
          scrollSpeed: 50
        }
      });
      
      // Update user's sheet count
      await updateDoc(doc(db, 'users', user.uid), {
        sheetCount: increment(1)
      });
      
      return sheetDoc.id;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateSheet = async (sheetId, updates) => {
    try {
      await updateDoc(doc(db, 'sheets', sheetId), {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteSheet = async (sheet) => {
    try {
      // Delete from Storage
      if (sheet.fileUrl) {
        const fileRef = ref(storage, sheet.fileUrl);
        await deleteObject(fileRef).catch(() => {
          // File might not exist, continue anyway
        });
      }
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'sheets', sheet.id));
      
      // Update user's sheet count
      if (user) {
        await updateDoc(doc(db, 'users', user.uid), {
          sheetCount: increment(-1)
        });
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const toggleFavorite = async (sheetId, currentValue) => {
    await updateSheet(sheetId, { isFavorite: !currentValue });
  };

  const toggleFolderFavorite = async (folderId, currentValue) => {
    try {
      await updateDoc(doc(db, 'folders', folderId), {
        isFavorite: !currentValue,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Move sheet to a folder
  const moveToFolder = async (sheetId, folderId) => {
    await updateSheet(sheetId, { folderId });
  };

  // Generate shareable link for a sheet
  const createShareLink = async (sheetId) => {
    try {
      const shareId = generateShareId();
      await updateSheet(sheetId, { 
        isPublic: true, 
        shareId 
      });
      return `${window.location.origin}/shared/${shareId}`;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Revoke share link
  const revokeShareLink = async (sheetId) => {
    await updateSheet(sheetId, { 
      isPublic: false, 
      shareId: null 
    });
  };

  // Get sheet by share ID (for viewing shared sheets)
  const getSharedSheet = async (shareId) => {
    try {
      const q = query(
        collection(db, 'sheets'),
        where('shareId', '==', shareId),
        where('isPublic', '==', true)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // Get sheets by folder
  const getSheetsByFolder = (folderId) => {
    if (!folderId) {
      return sheets.filter(s => !s.folderId);
    }
    return sheets.filter(s => s.folderId === folderId);
  };

  // ---- Link embeds ----

  // Extract YouTube video ID from any YouTube URL format
  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const re of patterns) {
      const m = url.match(re);
      if (m) return m[1];
    }
    return null;
  };

  const addLink = async ({ title, linkUrl, folderId = null, notes = '' }) => {
    if (!user?.uid) throw new Error('Must be logged in');
    const trimmedUrl = linkUrl.trim();
    const youtubeId = extractYouTubeId(trimmedUrl);
    const platform = youtubeId ? 'youtube' : 'web';
    const thumbnail = youtubeId
      ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
      : null;

    const docRef = await addDoc(collection(db, 'sheets'), {
      userId: user.uid,
      type: 'link',
      title: title.trim(),
      linkUrl: trimmedUrl,
      platform,
      embedId: youtubeId || null,
      thumbnail,
      notes,
      folderId,
      isFavorite: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return docRef.id;
  };

  return {
    sheets,
    folders,
    loading,
    error,
    currentFolder,
    setCurrentFolder,
    uploadSheet,
    addLink,
    updateSheet,
    deleteSheet,
    toggleFavorite,
    toggleFolderFavorite,
    createFolder,
    updateFolder,
    deleteFolder,
    moveToFolder,
    createShareLink,
    revokeShareLink,
    getSharedSheet,
    getSheetsByFolder,
    extractYouTubeId,
  };
};

// Helper functions
const generateShareId = () => {
  return Math.random().toString(36).substring(2, 10) + 
         Math.random().toString(36).substring(2, 10);
};

const getRandomFolderColor = () => {
  const colors = ['#C75B7A', '#6B9EBF', '#7AB87A', '#E8A84C', '#9B7AB8', '#5BA3A3'];
  return colors[Math.floor(Math.random() * colors.length)];
};

export default useSheets;

